import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Eye, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VenueStats = () => {
  const [params] = useSearchParams();
  const key = params.get('key') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Venue Stats · Wedding Video Essex';
    if (!key) { setError('Missing admin key in URL (?key=...)'); setLoading(false); return; }
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/venues/analytics/summary?key=${encodeURIComponent(key)}`);
        if (r.status === 403) { setError('Invalid admin key'); setLoading(false); return; }
        const d = await r.json();
        setData(d);
      } catch {
        setError('Failed to load stats');
      } finally {
        setLoading(false);
      }
    })();
  }, [key]);

  const totalVisits = data?.venues?.reduce((s, v) => s + v.total, 0) || 0;
  const totalBrevo = data?.venues?.reduce((s, v) => s + v.brevo, 0) || 0;

  return (
    <div data-testid="venue-stats" className="min-h-screen" style={{ backgroundColor: '#1A1A1A' }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 max-w-5xl">
        <div className="mb-10">
          <p className="text-gold text-xs uppercase tracking-widest font-semibold mb-2">Admin</p>
          <h1 className="font-serif text-4xl text-white mb-3">Venue page analytics</h1>
          <p className="text-white/50 text-sm">Visits per venue landing page, broken down by source. Emails from the wedding-fair funnel are tracked as <span className="text-gold">brevo</span>.</p>
        </div>

        {loading && <p className="text-white/50">Loading…</p>}
        {error && <p className="text-red-400 text-sm">{error}</p>}

        {data && (
          <>
            {/* Top stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest mb-2">
                  <Eye className="w-3.5 h-3.5" /> Total visits
                </div>
                <p className="font-serif text-3xl text-white">{totalVisits.toLocaleString()}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-widest mb-2">
                  <Mail className="w-3.5 h-3.5" /> Email click-throughs
                </div>
                <p className="font-serif text-3xl text-gold">{totalBrevo.toLocaleString()}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest mb-2">
                  <TrendingUp className="w-3.5 h-3.5" /> Venues with traffic
                </div>
                <p className="font-serif text-3xl text-white">{data.count}</p>
              </div>
            </div>

            {/* Leaderboard */}
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-12 px-5 py-3 border-b border-white/10 text-white/40 text-xs uppercase tracking-widest">
                <div className="col-span-6">Venue</div>
                <div className="col-span-2 text-right text-gold">Email</div>
                <div className="col-span-2 text-right">Direct</div>
                <div className="col-span-2 text-right">Total</div>
              </div>
              {data.venues.length === 0 && (
                <p className="text-white/40 text-sm p-8 text-center">No visits recorded yet.</p>
              )}
              {data.venues.map((v) => (
                <div key={v.slug} className="grid grid-cols-12 items-center px-5 py-3 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <div className="col-span-6">
                    <a href={`/venues/${v.slug}`} className="text-white hover:text-gold text-sm font-medium">{v.name}</a>
                    <p className="text-white/30 text-[10px] uppercase tracking-widest">{v.slug}</p>
                  </div>
                  <div className="col-span-2 text-right text-gold text-sm font-semibold">{v.brevo || 0}</div>
                  <div className="col-span-2 text-right text-white/70 text-sm">{v.sources?.direct || 0}</div>
                  <div className="col-span-2 text-right text-white text-sm font-semibold">{v.total}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VenueStats;
